import { useEffect, useRef, useState } from 'react';

import LoadingIndicator from '@components/LoadingIndicator/LoadingIndicator';
import CommonDialog from '@components/Modals/CommonDialog';
import { RequisitionReceiptContent } from '@components/Requisition/RequisitionReceiptContent/RequisitionReceiptContent';
import { Requisition } from '@customTypes/requisition';
import useRequisition from '@hooks/useRequisition';
import { getRequisitionDetail } from '@store/actions';
import { getReceiptPdfFilename, printReceiptPdf } from '@utils/downloadReceiptPdf';

interface IProps {
  requisition: Requisition;
  onClose: () => void;
}

export function OrderReceiptDialog({ requisition, onClose }: IProps) {
  const { requisitionId } = useRequisition(requisition);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [detail, setDetail] = useState<Requisition>();
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    getRequisitionDetail(requisition.requisition_id)
      .then(response => {
        if (active) setDetail(response);
      })
      .catch(() => {
        if (active) setError(true);
      });
    return () => {
      active = false;
    };
  }, [requisition.requisition_id]);

  const handlePrint = async () => {
    const element = contentRef.current;
    if (!element) return;

    await printReceiptPdf(element, getReceiptPdfFilename(detail ?? requisition));
  };

  return (
    <CommonDialog
      onClose={onClose}
      onConfirm={handlePrint}
      title='Order Receipt'
      modalProps={{
        'data-testid': 'orderReceiptDialog',
        'aria-labelledby': `order_receipt_${requisitionId}`,
        id: 'order-receipt-dialog',
        size: 'lg',
      }}
      cancelBtnProps={{
        'data-testid': 'closeOrderReceiptDialog',
        children: 'Close',
      }}
      confirmBtnProps={{ children: 'Print / Download', disabled: !detail }}
      titleProps={{
        id: `order_receipt_${requisitionId}`,
      }}
    >
      {!detail && !error && <LoadingIndicator />}
      {error && <p>Unable to load requisition data</p>}
      {detail && <RequisitionReceiptContent ref={contentRef} requisition={detail} />}
    </CommonDialog>
  );
}
